import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import {
  useGetConnectionsQuery,
  useConnectCalendarMutation,
  useDisconnectCalendarMutation,
  useLazyGetCalendarAuthUrlQuery,
  useGetCrmStatusQuery,
  useLazyGetCrmAuthUrlQuery,
  useDisconnectCrmMutation,
} from '../../../api/preCallApi';

export function useIntegrations() {
  const { data: connectionsData, isLoading: isConnsLoading } = useGetConnectionsQuery();
  const { data: crmStatusData, isLoading: isCrmLoading, refetch: refetchCrmStatus } = useGetCrmStatusQuery();
  const [connectCalendar] = useConnectCalendarMutation();
  const [disconnectCalendar] = useDisconnectCalendarMutation();
  const [triggerGetAuthUrl] = useLazyGetCalendarAuthUrlQuery();
  const [triggerGetCrmAuthUrl] = useLazyGetCrmAuthUrlQuery();
  const [disconnectCrm] = useDisconnectCrmMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalProvider, setModalProvider] = useState<'google' | 'outlook'>('google');
  const [manualEmail, setManualEmail] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);

  const isLoading = isConnsLoading || isCrmLoading;

  // Detect redirect back from CRM OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const crmSync = params.get('crm_sync');
    const crmError = params.get('crm_error');
    const provider = params.get('provider');
    const email = params.get('email');

    if (crmSync === 'success' && (provider === 'salesforce' || provider === 'hubspot')) {
      window.history.replaceState({}, '', window.location.pathname);
      refetchCrmStatus();
      const providerName = provider === 'salesforce' ? 'Salesforce' : 'HubSpot';
      Swal.fire({
        icon: 'success',
        title: `${providerName} Connected!`,
        text: `Successfully linked ${email || `your ${providerName} account`}. Pre-call briefs will now pull live CRM data.`,
        confirmButtonColor: '#38bdf8',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)',
      });
    } else if (crmError) {
      window.history.replaceState({}, '', window.location.pathname);
      Swal.fire({
        icon: 'error',
        title: 'Connection Failed',
        text: `Error: ${crmError.replace(/_/g, ' ')}. Please try again.`,
        confirmButtonColor: '#38bdf8',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)',
      });
    }
  }, [refetchCrmStatus]);

  const connections = connectionsData?.data || [];
  
  const sfStatus = crmStatusData?.data?.salesforce;
  const isSalesforceConnected = sfStatus?.connected ?? false;
  const sfEmail = sfStatus?.accountEmail ?? null;

  const hsStatus = crmStatusData?.data?.hubspot;
  const isHubspotConnected = hsStatus?.connected ?? false;
  const hubspotEmail = hsStatus?.accountEmail ?? null;

  // Google Calendar Connection Status
  const googleConns = connections.filter(c => c.provider === 'google');
  const isGoogleConnected = googleConns.length > 0;
  const googleEmail = isGoogleConnected ? googleConns[0].accountEmail : null;

  // Outlook Calendar Connection Status
  const outlookConns = connections.filter(c => c.provider === 'outlook');
  const isOutlookConnected = outlookConns.length > 0;
  const outlookEmail = isOutlookConnected ? outlookConns[0].accountEmail : null;

  const getCurrentUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
      return user.id || user.userId || user._id || null;
    } catch {
      return null;
    }
  };

  const handleConnectSalesforce = async () => {
    const userId = getCurrentUserId();
    if (!userId) {
      Swal.fire('Error', 'Cannot determine your user session. Please log in again.', 'error');
      return;
    }
    try {
      Swal.fire({
        title: 'Redirecting to Salesforce...',
        html: 'Preparing a secure OAuth connection...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const res = await triggerGetCrmAuthUrl({ provider: 'salesforce', state: userId }).unwrap();
      if (res?.success && res?.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('Failed to retrieve Salesforce auth URL.');
      }
    } catch (err: any) {
      Swal.fire('Connection Failed', err?.data?.message || err?.message || 'Could not initiate Salesforce OAuth.', 'error');
    }
  };

  const handleDisconnectSalesforce = async () => {
    const result = await Swal.fire({
      title: 'Disconnect Salesforce?',
      text: 'Pre-call briefs will no longer pull live CRM data.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, disconnect',
      customClass: {
        confirmButton: 'swal-danger'
      },
      background: 'var(--admin-card-bg)',
      color: 'var(--admin-card-text)'
    });
    if (result.isConfirmed) {
      try {
        await disconnectCrm('salesforce').unwrap();
        refetchCrmStatus();
        Swal.fire({
          title: 'Disconnected!',
          text: 'Salesforce has been disconnected.',
          icon: 'success',
          confirmButtonColor: '#38bdf8',
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)'
        });
      } catch {
        Swal.fire('Error', 'Failed to disconnect Salesforce.', 'error');
      }
    }
  };

  const handleConnectHubspot = async () => {
    const userId = getCurrentUserId();
    if (!userId) {
      Swal.fire('Error', 'Cannot determine your user session. Please log in again.', 'error');
      return;
    }
    try {
      Swal.fire({
        title: 'Redirecting to HubSpot...',
        html: 'Preparing a secure OAuth connection...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const res = await triggerGetCrmAuthUrl({ provider: 'hubspot', state: userId }).unwrap();
      if (res?.success && res?.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('Failed to retrieve HubSpot auth URL.');
      }
    } catch (err: any) {
      Swal.fire('Connection Failed', err?.data?.message || err?.message || 'Could not initiate HubSpot OAuth.', 'error');
    }
  };

  const handleDisconnectHubspot = async () => {
    const result = await Swal.fire({
      title: 'Disconnect HubSpot?',
      text: 'Pre-call briefs will no longer pull live CRM data.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, disconnect',
      customClass: {
        confirmButton: 'swal-danger'
      },
      background: 'var(--admin-card-bg)',
      color: 'var(--admin-card-text)'
    });
    if (result.isConfirmed) {
      try {
        await disconnectCrm('hubspot').unwrap();
        refetchCrmStatus();
        Swal.fire({
          title: 'Disconnected!',
          text: 'HubSpot has been disconnected.',
          icon: 'success',
          confirmButtonColor: '#38bdf8',
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)'
        });
      } catch {
        Swal.fire('Error', 'Failed to disconnect HubSpot.', 'error');
      }
    }
  };

  const handleOAuthConnect = async (providerOverride?: string) => {
    const providerToUse = providerOverride || modalProvider;
    try {
      Swal.fire({
        title: 'Redirecting to Calendar Provider...',
        html: 'Preparing secure credentials connection...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const res = await triggerGetAuthUrl({ provider: providerToUse }).unwrap();
      if (res?.success && res?.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('Failed to retrieve authentication URL.');
      }
    } catch (err: any) {
      Swal.fire('OAuth Link Failed', err?.data?.message || err?.message || 'Could not fetch authorization link.', 'error');
    }
  };

  const handleManualConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualEmail.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(manualEmail.trim())) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Email',
        text: 'Please enter a valid email address.',
      });
      return;
    }

    try {
      await connectCalendar({ provider: modalProvider, email: manualEmail }).unwrap();
      setIsModalOpen(false);
      setManualEmail('');
      setShowManualInput(false);
      Swal.fire({
        icon: 'success',
        title: 'Calendar Connected',
        text: `Successfully synced ${modalProvider === 'google' ? 'Google Calendar' : 'Outlook'} for ${manualEmail}`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Connect',
        text: err?.data?.message || 'Something went wrong.',
      });
    }
  };

  const handleDisconnectCalendar = async (provider: 'google' | 'outlook') => {
    const targetConns = connections.filter(c => c.provider === provider);
    if (targetConns.length === 0) return;
    const conn = targetConns[0];

    const result = await Swal.fire({
      title: 'Disconnect calendar?',
      text: `This will stop automatically fetching upcoming meetings from ${conn.accountEmail}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, disconnect',
      customClass: {
        confirmButton: 'swal-danger'
      },
      background: 'var(--admin-card-bg)',
      color: 'var(--admin-card-text)'
    });

    if (result.isConfirmed) {
      try {
        await disconnectCalendar(conn.id).unwrap();
        Swal.fire({
          title: 'Disconnected!',
          text: 'Your calendar connection has been removed.',
          icon: 'success',
          confirmButtonColor: '#38bdf8',
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)'
        });
      } catch (err) {
        Swal.fire('Error', 'Failed to disconnect calendar connection.', 'error');
      }
    }
  };

  const openConnectModal = (provider: 'google' | 'outlook') => {
    setModalProvider(provider);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setManualEmail('');
    setShowManualInput(false);
  };

  return {
    isLoading,
    isGoogleConnected,
    googleEmail,
    isOutlookConnected,
    outlookEmail,
    isSalesforceConnected,
    sfEmail,
    isHubspotConnected,
    hubspotEmail,
    isModalOpen,
    modalProvider,
    manualEmail,
    setManualEmail,
    showManualInput,
    setShowManualInput,
    handleConnectSalesforce,
    handleDisconnectSalesforce,
    handleConnectHubspot,
    handleDisconnectHubspot,
    handleOAuthConnect,
    handleManualConnect,
    handleDisconnectCalendar,
    openConnectModal,
    closeModal
  };
}
